import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useElectron } from '@/context/ElectronContext';
import { useProjectDirectories } from '@/context/ProjectDirectoriesContext';
import { desktopBridge } from '@/services/desktopBridge';
import {
  GetProjectLibraryFilesDocument,
  type GetProjectLibraryFilesQueryData,
  type GetProjectLibraryFilesQueryVariables,
} from '@/generated/graphql';
import {
  scanProjectLibrary,
  type LibraryFilter,
  type LibraryProject,
  type LibraryProjectStatus,
} from '../utils/projectLibraryFilters';

const EMPTY_STATUSES: Record<string, LibraryProjectStatus> = {};

export function useProjectLibraryStatus(
  projects: readonly LibraryProject[],
  filter: LibraryFilter
) {
  const client = useApolloClient();
  const { isElectron, electronAPI } = useElectron();
  const { getStoredProjectPath } = useProjectDirectories();
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  const request = useMemo(
    () => ({
      projects,
      filter,
      revision,
      client,
      isElectron,
      electronAPI,
      getStoredProjectPath,
    }),
    [
      projects,
      filter,
      revision,
      client,
      isElectron,
      electronAPI,
      getStoredProjectPath,
    ]
  );
  const [state, setState] = useState<{
    request: typeof request | null;
    statuses: Record<string, LibraryProjectStatus>;
  }>({ request: null, statuses: EMPTY_STATUSES });

  useEffect(() => {
    const {
      projects,
      filter,
      client,
      isElectron,
      electronAPI,
      getStoredProjectPath,
    } = request;
    if (filter === 'all') return;
    let cancelled = false;
    async function scan() {
      let checker;
      if (filter === 'outdated') {
        if (isElectron && electronAPI) {
          checker = electronAPI.requestSyncCheck.bind(electronAPI);
        } else if (!isElectron) {
          try {
            if (await desktopBridge.isDesktopAvailable()) {
              checker = desktopBridge.requestSyncCheck.bind(desktopBridge);
            }
          } catch {
            /* Remain unverified while the desktop bridge is unavailable. */
          }
        }
      }
      const statuses = await scanProjectLibrary({
        projects:
          filter === 'outdated'
            ? checker
              ? projects.filter((project) => getStoredProjectPath(project._id))
              : []
            : projects,
        getPath: getStoredProjectPath,
        checkFiles: checker,
        isCancelled: () => cancelled,
        loadFiles: async (projectId, version) => {
          const response = await client.query<
            GetProjectLibraryFilesQueryData,
            GetProjectLibraryFilesQueryVariables
          >({
            query: GetProjectLibraryFilesDocument,
            variables: { projectId, version },
            // Rechecks must use the server's latest manifest, not a stale cache.
            fetchPolicy: 'network-only',
          });
          if (!response.data.projectVersion?.files)
            throw new Error('Manifest unavailable');
          return response.data.projectVersion.files;
        },
      });
      if (!cancelled) setState({ request, statuses });
    }
    void scan();
    return () => {
      cancelled = true;
    };
  }, [request]);

  useEffect(() => {
    if (filter !== 'outdated') return;
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [filter, refresh]);

  const loading = filter !== 'all' && state.request !== request;
  return {
    statuses: state.request === request ? state.statuses : EMPTY_STATUSES,
    loading,
    refresh,
  };
}
